import { encodeCp437 } from "./cp437";
import type { Align, Block, MonoBitmap, PaperWidth, TextStyle } from "./doc";
import { type Line, layout } from "./layout";

/**
 * ESC/POS command encoder (Epson compatible, as used by the KPC307-UEWB).
 *
 * Moved from the proof of concept (`web/shared/escpos.ts`). Its golden byte
 * fixtures (`fixtures/poc/*.bin`) still pass byte for byte.
 */

const ESC = 0x1b;
const GS = 0x1d;
const DLE = 0x10;
const LF = 0x0a;

function clamp(n: number, lo: number, hi: number): number {
	return Math.max(lo, Math.min(hi, Math.round(n)));
}

export const CMD = {
	init: [ESC, 0x40],
	codepage: (n: number) => [ESC, 0x74, n],
	align: (a: Align) => [ESC, 0x61, a === "center" ? 1 : a === "right" ? 2 : 0],
	font: (b: boolean) => [ESC, 0x4d, b ? 1 : 0],
	bold: (on: boolean) => [ESC, 0x45, on ? 1 : 0],
	underline: (on: boolean) => [ESC, 0x2d, on ? 1 : 0],
	invert: (on: boolean) => [GS, 0x42, on ? 1 : 0],
	size: (w: number, h: number) => [GS, 0x21, ((clamp(w, 1, 8) - 1) << 4) | (clamp(h, 1, 8) - 1)],
	feedLines: (n: number) => [ESC, 0x64, clamp(n, 0, 255)],
	/** Feeds to the cutter position, then cuts. */
	cut: (full = false) => [GS, 0x56, full ? 0x41 : 0x42, 0],
	/** Buzzer: ESC B n t, n beeps of t × 50 ms (common on Chinese ESC/POS printers). */
	beep: (times = 1, t = 2) => [ESC, 0x42, clamp(times, 1, 9), clamp(t, 1, 9)],
	/** Pulses cash drawer pin 2: on 25 × 2 ms, off 250 × 2 ms. */
	drawer: () => [ESC, 0x70, 0, 25, 250],
	/** Real-time status request, n = 1 to 4. The printer replies with one byte. */
	status: (n: 1 | 2 | 3 | 4) => [DLE, 0x04, n],
	/** Prints the printer's built-in self-test page. */
	selfTest: () => [GS, 0x28, 0x41, 2, 0, 0, 2],
};

function styleBytes(s: TextStyle): number[] {
	return [
		...CMD.font(s.font === "B"),
		...CMD.bold(!!s.bold),
		...CMD.underline(!!s.underline),
		...CMD.invert(!!s.invert),
		...CMD.size(s.w ?? 1, s.h ?? 1),
	];
}

/** UTF-8 bytes of a string. Written out so the package needs no `TextEncoder` global. */
export function utf8(s: string): number[] {
	const out: number[] = [];
	for (const ch of s) {
		const c = ch.codePointAt(0) ?? 0;
		if (c < 0x80) out.push(c);
		else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
		else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
		else
			out.push(
				0xf0 | (c >> 18),
				0x80 | ((c >> 12) & 0x3f),
				0x80 | ((c >> 6) & 0x3f),
				0x80 | (c & 0x3f),
			);
	}
	return out;
}

/** A native QR code (`GS ( k`): model 2, error correction M. */
export function qrBytes(data: string, size = 6): number[] {
	const bytes = utf8(data);
	const len = bytes.length + 3;
	return [
		...[GS, 0x28, 0x6b, 4, 0, 0x31, 0x41, 0x32, 0], // model 2
		...[GS, 0x28, 0x6b, 3, 0, 0x31, 0x43, clamp(size, 1, 16)], // module size
		...[GS, 0x28, 0x6b, 3, 0, 0x31, 0x45, 0x31], // error correction M
		...[GS, 0x28, 0x6b, len & 0xff, len >> 8, 0x31, 0x50, 0x30, ...bytes], // store
		...[GS, 0x28, 0x6b, 3, 0, 0x31, 0x51, 0x30], // print
	];
}

/** A native CODE128 barcode (`GS k`) with the text printed below. */
export function barcodeBytes(data: string, height = 80): number[] {
	const payload = encodeCp437(`{B${data}`);
	return [
		...[GS, 0x68, clamp(height, 1, 255)],
		...[GS, 0x77, 2], // module width
		...[GS, 0x48, 2], // text below
		...[GS, 0x66, 0], // text in font A
		...[GS, 0x6b, 73, payload.length, ...payload], // CODE128
	];
}

/** A `GS v 0` raster image, sent in horizontal bands to stay within the printer's buffer. */
export function rasterBytes(bmp: MonoBitmap, bandRows = 128): number[] {
	const bytesPerRow = Math.ceil(bmp.width / 8);
	const out: number[] = [];
	for (let y = 0; y < bmp.height; y += bandRows) {
		const rows = Math.min(bandRows, bmp.height - y);
		out.push(GS, 0x76, 0x30, 0, bytesPerRow & 0xff, bytesPerRow >> 8, rows & 0xff, rows >> 8);
		const start = y * bytesPerRow;
		for (let i = start; i < start + rows * bytesPerRow; i++) out.push(bmp.data[i] ?? 0);
	}
	return out;
}

export type EncodeOptions = {
	/** ESC t code page number; 0 is CP437. */
	codepage?: number;
};

/** ESC/POS bytes for laid-out lines. */
export function encodeLines(lines: Line[], opts: EncodeOptions = {}): Uint8Array {
	const out: number[] = [...CMD.init, ...CMD.codepage(opts.codepage ?? 0)];
	const push = (bytes: number[]) => {
		for (const b of bytes) out.push(b);
	};
	for (const l of lines) {
		switch (l.t) {
			case "line":
				push([...styleBytes(l.style), ...encodeCp437(l.text), LF]);
				break;
			case "feed":
				push(CMD.feedLines(l.lines));
				break;
			case "qr":
				push([
					...CMD.align(l.align ?? "center"),
					...qrBytes(l.data, l.size),
					LF,
					...CMD.align("left"),
				]);
				break;
			case "barcode":
				push([
					...CMD.align(l.align ?? "center"),
					...barcodeBytes(l.data, l.height),
					LF,
					...CMD.align("left"),
				]);
				break;
			case "bitmap":
				push([...CMD.align(l.align ?? "center"), ...rasterBytes(l.bitmap), ...CMD.align("left")]);
				break;
			case "cut":
				push(CMD.cut(l.full));
				break;
			case "beep":
				push(CMD.beep(l.times));
				break;
			case "drawer":
				push(CMD.drawer());
				break;
		}
	}
	push(styleBytes({}));
	return Uint8Array.from(out);
}

/** Text mode: renders blocks with the printer's built-in fonts. */
export function encodeBlocks(
	blocks: Block[],
	paper: PaperWidth,
	opts: EncodeOptions = {},
): Uint8Array {
	return encodeLines(layout(blocks, paper), opts);
}
